<template>
  <div class="ad-create-log">
    <div class="page-toolbar">
      <div>
        <h2>批量创建日志</h2>
        <div class="sub-text">查看批量创建广告的批次和单条广告结果</div>
      </div>
      <div class="toolbar-actions">
        <el-input
          v-model.trim="batchIdInput"
          clearable
          placeholder="输入批次号查看明细"
          style="width: 300px"
          @keyup.enter.native="handleOpenBatchId"
        />
        <el-button type="primary" icon="el-icon-search" @click="handleOpenBatchId">查看</el-button>
        <el-button icon="el-icon-refresh" :loading="batchLoading" @click="loadBatches">刷新</el-button>
      </div>
    </div>

    <el-row :gutter="16">
      <el-col :span="9">
        <el-card shadow="never" class="panel-card">
          <div slot="header" class="card-header">
            <span>批次列表</span>
            <span class="muted-text">共 {{ batchPagination.total }} 个批次</span>
          </div>
          <el-table
            :data="batchList"
            v-loading="batchLoading"
            stripe
            highlight-current-row
            row-key="batchId"
            max-height="680"
            @row-click="handleBatchRowClick"
          >
            <el-table-column label="批次" min-width="220" show-overflow-tooltip>
              <template slot-scope="{ row }">
                <div class="batch-id">{{ row.batchId }}</div>
                <div class="batch-time">{{ formatDateTime(row.startedAt) }}</div>
              </template>
            </el-table-column>
            <el-table-column label="状态" width="110" align="center">
              <template slot-scope="{ row }">
                <el-tag size="mini" :type="getStatusTagType(row.status)">
                  {{ getStatusText(row.status) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="成功/失败" width="110" align="center">
              <template slot-scope="{ row }">
                {{ row.successCount || 0 }}/{{ row.failCount || 0 }}
              </template>
            </el-table-column>
          </el-table>

          <el-pagination
            class="pagination"
            small
            layout="prev, pager, next"
            :current-page="batchPagination.page"
            :page-size="batchPagination.pageSize"
            :total="batchPagination.total"
            @current-change="handleBatchPageChange"
          />
        </el-card>
      </el-col>

      <el-col :span="15">
        <el-card shadow="never" class="panel-card">
          <div slot="header" class="card-header">
            <span>广告明细</span>
            <span v-if="selectedBatchId" class="muted-text">{{ selectedBatchId }}</span>
          </div>

          <el-empty v-if="!selectedBatchId && !itemLoading" description="请选择左侧批次或输入批次号" />

          <template v-else>
            <el-row :gutter="12" class="summary-row">
              <el-col :span="6">
                <div class="summary-item">
                  <div class="summary-label">总数</div>
                  <div class="summary-value">{{ selectedBatchSummary.totalCount || itemPagination.total || 0 }}</div>
                </div>
              </el-col>
              <el-col :span="6">
                <div class="summary-item">
                  <div class="summary-label">成功</div>
                  <div class="summary-value success">{{ selectedBatchSummary.successCount || 0 }}</div>
                </div>
              </el-col>
              <el-col :span="6">
                <div class="summary-item">
                  <div class="summary-label">失败</div>
                  <div class="summary-value danger">{{ selectedBatchSummary.failCount || 0 }}</div>
                </div>
              </el-col>
              <el-col :span="6">
                <div class="summary-item">
                  <div class="summary-label">耗时</div>
                  <div class="summary-value small">{{ formatDuration(selectedBatchSummary.durationMs) }}</div>
                </div>
              </el-col>
            </el-row>

            <el-table
              :data="itemList"
              v-loading="itemLoading"
              stripe
              row-key="id"
              max-height="560"
              empty-text="暂无明细"
            >
              <el-table-column prop="batchIndex" label="序号" width="70" align="center" />
              <el-table-column label="广告" min-width="240" show-overflow-tooltip>
                <template slot-scope="{ row }">
                  <div class="ad-name">{{ row.adName || '-' }}</div>
                  <div class="sub-text">{{ row.smartPlusAdId || row.smartPlusAdIds || '-' }}</div>
                </template>
              </el-table-column>
              <el-table-column prop="advertiserId" label="广告主" width="150" show-overflow-tooltip />
              <el-table-column label="广告组" width="180" show-overflow-tooltip>
                <template slot-scope="{ row }">
                  <div>{{ row.adgroupName || '-' }}</div>
                  <div class="sub-text">{{ row.adgroupId || '-' }}</div>
                </template>
              </el-table-column>
              <el-table-column label="状态" width="100" align="center">
                <template slot-scope="{ row }">
                  <el-tag size="mini" :type="getStatusTagType(row.status)">
                    {{ getStatusText(row.status) }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="responseCode" label="Code" width="80" align="center" />
              <el-table-column label="耗时" width="120" align="right">
                <template slot-scope="{ row }">
                  {{ formatDuration(row.totalDurationMs) }}
                </template>
              </el-table-column>
              <el-table-column prop="errorMessage" label="失败原因" min-width="220" show-overflow-tooltip />
            </el-table>

            <el-pagination
              class="pagination"
              layout="total, sizes, prev, pager, next, jumper"
              :current-page="itemPagination.page"
              :page-size="itemPagination.pageSize"
              :page-sizes="[50, 100, 200]"
              :total="itemPagination.total"
              @size-change="handleItemSizeChange"
              @current-change="handleItemPageChange"
            />
          </template>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { adCreateLogApi } from '@/api/adCreateLog'

export default {
  name: 'AdCreateLog',
  data() {
    return {
      batchIdInput: '',
      batchLoading: false,
      itemLoading: false,
      batchList: [],
      itemList: [],
      selectedBatchId: '',
      selectedBatchSummary: {},
      batchPagination: {
        page: 1,
        pageSize: 20,
        total: 0
      },
      itemPagination: {
        page: 1,
        pageSize: 100,
        total: 0
      }
    }
  },
  created() {
    this.batchIdInput = this.$route.query.batchId || ''
    this.loadBatches().then(() => {
      if (this.batchIdInput) {
        this.openBatch(this.batchIdInput)
      }
    })
  },
  methods: {
    async loadBatches() {
      this.batchLoading = true
      try {
        const response = await adCreateLogApi.getBatches(this.batchPagination)
        const data = response.data || {}
        this.batchList = data.list || []
        this.batchPagination.total = Number(data.total || 0)

        if (!this.selectedBatchId && this.batchList.length > 0 && !this.batchIdInput) {
          this.openBatch(this.batchList[0].batchId, this.batchList[0])
        }
      } catch (error) {
        this.$message.error('加载批次日志失败: ' + error.message)
      } finally {
        this.batchLoading = false
      }
    },
    handleBatchRowClick(row) {
      this.openBatch(row.batchId, row)
    },
    handleOpenBatchId() {
      if (!this.batchIdInput) {
        this.$message.warning('请输入批次号')
        return
      }
      this.openBatch(this.batchIdInput)
    },
    openBatch(batchId, batchSummary = null) {
      this.selectedBatchId = batchId
      this.batchIdInput = batchId
      this.selectedBatchSummary = batchSummary || this.batchList.find(item => item.batchId === batchId) || {}
      this.itemPagination.page = 1
      this.loadItems()
    },
    async loadItems() {
      if (!this.selectedBatchId) return
      this.itemLoading = true
      try {
        const response = await adCreateLogApi.getBatchItems(this.selectedBatchId, this.itemPagination)
        const data = response.data || {}
        this.itemList = data.list || []
        this.itemPagination.total = Number(data.total || 0)
      } catch (error) {
        this.$message.error('加载广告明细失败: ' + error.message)
      } finally {
        this.itemLoading = false
      }
    },
    handleBatchPageChange(page) {
      this.batchPagination.page = page
      this.loadBatches()
    },
    handleItemSizeChange(pageSize) {
      this.itemPagination.pageSize = pageSize
      this.itemPagination.page = 1
      this.loadItems()
    },
    handleItemPageChange(page) {
      this.itemPagination.page = page
      this.loadItems()
    },
    getStatusText(status) {
      const map = {
        RUNNING: '进行中',
        SUCCESS: '成功',
        FAILED: '失败',
        PARTIAL_FAILED: '部分失败'
      }
      return map[status] || status || '-'
    },
    getStatusTagType(status) {
      const map = {
        RUNNING: 'warning',
        SUCCESS: 'success',
        FAILED: 'danger',
        PARTIAL_FAILED: 'warning'
      }
      return map[status] || 'info'
    },
    formatDuration(value) {
      const ms = Number(value || 0)
      if (!ms) return '-'
      if (ms < 1000) return `${ms}ms`
      return `${(ms / 1000).toFixed(1)}s`
    },
    formatDateTime(value) {
      if (!value) return '-'
      return String(value).replace('T', ' ').slice(0, 19)
    }
  }
}
</script>

<style scoped>
.ad-create-log {
  min-height: calc(100vh - 40px);
  padding: 20px;
  background: #f5f7fa;
}

.page-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.page-toolbar h2 {
  margin: 0 0 6px;
  color: #303133;
  font-size: 22px;
  font-weight: 600;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.panel-card {
  border-radius: 6px;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.muted-text,
.sub-text,
.batch-time {
  color: #909399;
  font-size: 12px;
}

.batch-id,
.ad-name {
  color: #303133;
  font-size: 13px;
  font-weight: 600;
}

.summary-row {
  margin-bottom: 12px;
}

.summary-item {
  padding: 12px;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background: #fafbfc;
}

.summary-label {
  color: #909399;
  font-size: 12px;
  margin-bottom: 6px;
}

.summary-value {
  color: #303133;
  font-size: 20px;
  font-weight: 600;
}

.summary-value.small {
  font-size: 16px;
}

.summary-value.success {
  color: #67c23a;
}

.summary-value.danger {
  color: #f56c6c;
}

.pagination {
  margin-top: 14px;
  text-align: right;
}
</style>
